import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { CATEGORIES } from "../data/products";
import { ProductCard } from "./ProductCard";
import { QuickView } from "./QuickView";

export const ProductGrid = ({ products, favorites, onToggleFavorite }) => {
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [quickView, setQuickView] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter(
      (p) =>
        (category === "all" || p.category === category) &&
        (!q || p.title.toLowerCase().includes(q) || p.category.toLowerCase().includes(q))
    );
  }, [products, category, query]);

  const isDefault = category === "all" && !query.trim();

  return (
    <section id="products" className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FFB800]">The collection</p>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50">
          This week's curated picks
        </h2>
      </motion.div>

      <div id="categories" className="mt-10 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Product categories">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={c.testid}
              role="tab"
              aria-selected={category === c.key}
              onClick={() => setCategory(c.key)}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-all duration-200 active:scale-95 ${
                category === c.key
                  ? "bg-[#FF9900] text-[#0B0C0E]"
                  : "border border-white/10 text-slate-300 hover:border-[#FF9900]/40 hover:text-[#FFB800]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative md:w-72">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="product-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search finds..."
            className="w-full rounded-full border border-white/10 bg-[#121418] py-2.5 pl-10 pr-4 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-[#FF9900]/60"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <p data-testid="products-empty-state" className="mt-16 text-center text-slate-400">
          No finds match your search. Try another keyword.
        </p>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-6 [grid-auto-flow:dense] md:grid-cols-2 lg:grid-cols-4 lg:gap-7">
          {filtered.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              index={i}
              featured={isDefault && i === 0}
              onQuickView={setQuickView}
              isFavorite={favorites.includes(p.id)}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </div>
      )}

      <QuickView product={quickView} onClose={() => setQuickView(null)} />
    </section>
  );
};
